"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.feedrEnvironmentToMicroFrontendS3Uri = exports.feedrEnvironmentToAwsProfile = exports.feedrEnvironmentToAwsRegion = exports.feedrEnvironmentToLegacyFormat = exports.feedrEnvironmentToAwsAccount = exports.feedrEnvironmentSchema = exports.feedrDevEnvironmentSchema = void 0;
const ts_pattern_1 = require("ts-pattern");
const zod_1 = require("zod");
/**
 * Use this to validate whether a value is a supported Feedr environment other than prod. E.g.
 * ```
 * feedrEnvironmentSchema.parse("test") // works ✅
 * feedrEnvironmentSchema.parse("prod") // throws runtime error ❌
 * feedrEnvironmentSchema.parse("foo") // throws runtime error ❌
 * ```
 */
exports.feedrDevEnvironmentSchema = zod_1.z.union([
    zod_1.z.literal("test"),
    zod_1.z.literal("stage"),
    zod_1.z.literal("demo"),
]);
/**
 * Use this to validate whether a value is a supported Feedr environment. E.g.
 * ```
 * feedrEnvironmentSchema.parse("prod") as string // works ✅
 * feedrEnvironmentSchema.parse("foo") // throws runtime error ❌
 * ```
 */
exports.feedrEnvironmentSchema = zod_1.z.union([
    exports.feedrDevEnvironmentSchema,
    zod_1.z.literal("prod"),
]);
const feedrEnvironmentToAwsAccount = (feedrEnvironment) => (0, ts_pattern_1.match)(feedrEnvironment)
    .with("test", () => ({ name: "test", id: 911462866373 }))
    .with("stage", "demo", () => ({ name: "stage", id: 989953252203 }))
    .with("prod", () => ({ name: "prod", id: 774441142314 }))
    .exhaustive();
exports.feedrEnvironmentToAwsAccount = feedrEnvironmentToAwsAccount;
/**
 * Historically each environment was identified by a combination of two values: `ENVIRONNEMENT` and `DEPLOYMENT`.
 * E.g. `demo` environment has the values `ENVIRONMENT = "stage"` and `DEPLOYMENT = "demo"`.
 * This is confusing, hence going forwards we're using the `FeedrEnvironment` type instead.
 *
 * This function allows converting values to the legacy format,
 * which is useful for deriving names/urls of old AWS resources that use legacy convention.
 * ```
 * feedrEnvironmentToLegacyFormat("demo") == { ENVIRONMENT = "stage"; DEPLOYMENT = "demo" }
 * ```
 */
const feedrEnvironmentToLegacyFormat = (feedrEnvironment) => ({
    ENVIRONMENT: (0, exports.feedrEnvironmentToAwsAccount)(feedrEnvironment).name,
    DEPLOYMENT: (0, ts_pattern_1.match)(feedrEnvironment)
        .with("test", "stage", "prod", () => "blue")
        .with("demo", () => "demo")
        .exhaustive(),
});
exports.feedrEnvironmentToLegacyFormat = feedrEnvironmentToLegacyFormat;
const feedrEnvironmentToAwsRegion = (feedrEnvironment) => (0, ts_pattern_1.match)(feedrEnvironment)
    .with("test", "stage", "demo", "prod", () => "eu-west-2")
    .exhaustive();
exports.feedrEnvironmentToAwsRegion = feedrEnvironmentToAwsRegion;
/**
 * When using remote resources locally, some environments (e.g. `stage`) require admin role.
 * This function determines the relevant AWS profile that has the needed role depending on the given environment.
 */
const feedrEnvironmentToAwsProfile = (feedrEnvironment) => (0, ts_pattern_1.match)((0, exports.feedrEnvironmentToLegacyFormat)(feedrEnvironment).ENVIRONMENT)
    .with("test", (name) => name)
    .with("stage", "prod", (name) => `${name}_admin`)
    .exhaustive();
exports.feedrEnvironmentToAwsProfile = feedrEnvironmentToAwsProfile;
const feedrEnvironmentToMicroFrontendS3Uri = (feedrEnvironment) => {
    const awsAccount = (0, exports.feedrEnvironmentToAwsAccount)(feedrEnvironment);
    const { DEPLOYMENT } = (0, exports.feedrEnvironmentToLegacyFormat)(feedrEnvironment);
    return `s3://feedr-frontends-${awsAccount.name}-${DEPLOYMENT}`;
};
exports.feedrEnvironmentToMicroFrontendS3Uri = feedrEnvironmentToMicroFrontendS3Uri;
